import { Injectable, inject, computed, effect, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { I18nService } from './i18n.service';
import { debounceTime, distinctUntilChanged } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ReactiveTranslateService {
  private translate = inject(TranslateService);
  private i18nService = inject(I18nService);
  
  private signalCache = new Map<string, any>();
  
  // Signal para forçar re-render das traduções
  private translationVersion = signal(0);

  constructor() {
    // Effect que dispara quando idioma muda
    effect(() => {
      const currentLang = this.i18nService.idioma().code;
      
      // ✅ AGUARDAR TRADUÇÕES CARREGAREM COMPLETAMENTE
      this.translate.use(currentLang).subscribe(() => {
        // Aguarda um ciclo extra para garantir que tudo foi carregado
        setTimeout(() => {
          this.translationVersion.update(v => v + 1);
        }, 100); // Delay maior para garantir carregamento completo
      });
      
      // Limpa cache antigo
      this.clearCache();
    });
  }

  /**
   * Método principal - Cria signal reativo para uma chave
   * @param key Chave da tradução
   * @param initialValue Valor inicial (padrão: '')
   * @example
   * // Uso simples
   * title = this.rt.t('PAGE.TITLE');
   * subtitle = this.rt.t('PAGE.SUBTITLE', 'Carregando...');
   * 
   * // No template: {{ title() }} - {{ subtitle() }}
   */
  t(key: string, initialValue: string = '') {
    const cacheKey = `${key}|${initialValue}`;
    
    if (!this.signalCache.has(cacheKey)) {
      const signal = computed(() => {
        // ✅ DUPLO TRIGGER: idioma + versão de tradução
        this.i18nService.idioma();
        this.translationVersion(); // Força re-render quando tradução muda
        
        // Buscar tradução atual
        const translation = this.translate.instant(key);
        
        // ✅ MELHOR VALIDAÇÃO: verifica se realmente traduziu
        const isTranslated = translation && translation !== key && translation.trim() !== '';
        
        // Se traduziu corretamente, usar tradução, senão usar initialValue ou a chave base
        if (isTranslated) {
          return translation;
        } else {
          // Se não tem initialValue, usar último pedaço da chave como fallback
          return initialValue || key.split('.').pop() || key;
        }
      });
      
      this.signalCache.set(cacheKey, signal);
    }
    
    return this.signalCache.get(cacheKey);
  }

  /**
   * Cria computed signal para múltiplas traduções em array
   * @param keys Array de chaves
   * @example
   * // Uso para array de strings
   * labels = this.rt.getBatch(['SAVE', 'CANCEL', 'DELETE']);
   * 
   * // No template: 
   * // <button>{{ labels()[0] }}</button> <!-- SAVE -->
   * // <button>{{ labels()[1] }}</button> <!-- CANCEL -->
   */
  getBatch(keys: string[]) {
    return computed(() => keys.map(key => this.t(key)()));
  }

  /**
   * Cria computed signal para objeto de traduções
   * @param keyMap Objeto com propriedade -> chave de tradução
   * @example
   * // Uso para objeto de labels
   * formLabels = this.rt.getObject({
   *   name: 'FORM.NAME',
   *   email: 'FORM.EMAIL',
   *   password: 'FORM.PASSWORD'
   * });
   * 
   * // No template: 
   * // <label>{{ formLabels().name }}</label>
   * // <label>{{ formLabels().email }}</label>
   */
  getObject<T extends Record<string, string>>(keyMap: T) {
    return computed(() => {
      const result: Record<string, string> = {};
      for (const [prop, key] of Object.entries(keyMap)) {
        result[prop] = this.t(key)();
      }
      return result as { [K in keyof T]: string };
    });
  }

  /**
   * Helper para criar estruturas complexas reativas
   * @param structure Função que recebe translateFn e retorna estrutura
   * @example
   * // Uso para estruturas complexas
   * stats = this.rt.createStructured((t) => [
   *   { number: 500, label: t('STATS.USERS'), suffix: '+' },
   *   { number: 98, label: t('STATS.SUCCESS'), suffix: '%' },
   * ]);
   * 
   * teamMembers = this.rt.createStructured((t) => [
   *   { name: t('TEAM.JOHN.NAME'), role: t('TEAM.JOHN.ROLE') },
   *   { name: t('TEAM.JANE.NAME'), role: t('TEAM.JANE.ROLE') },
   * ]);
   */
  createStructured<TResult>(
    structure: (translateFn: (key: string) => string) => TResult
  ) {
    return computed(() => {
      const translateFn = (key: string) => this.t(key)();
      return structure(translateFn);
    });
  }

  /**
   * Helper para arrays com template - transforma array de dados adicionando traduções
   * @param template Array de dados base
   * @param keyMapper Função que mapeia dados para chaves de tradução
   * @example
   * // Uso para listas com dados fixos
   * menuItems = this.rt.createArray(
   *   [
   *     { id: 'home', icon: 'house' },
   *     { id: 'profile', icon: 'user' },
   *     { id: 'settings', icon: 'gear' }
   *   ],
   *   (item) => ({ 
   *     label: `MENU.${item.id.toUpperCase()}` 
   *   })
   * );
   * 
   * // Resultado: [
   * //   { id: 'home', icon: 'house', label: 'Início' },
   * //   { id: 'profile', icon: 'user', label: 'Perfil' },
   * //   { id: 'settings', icon: 'gear', label: 'Configurações' }
   * // ]
   */
  createArray<T>(
    template: T[],
    keyMapper: (item: T, index: number) => Record<string, string>
  ) {
    return computed(() => 
      template.map((item, index) => {
        const keyMap = keyMapper(item, index);
        const result: any = { ...item };
        
        for (const [prop, key] of Object.entries(keyMap)) {
          result[prop] = this.t(key)();
        }
        
        return result;
      })
    );
  }

  // Métodos utilitários
  private clearCache() {
    this.signalCache.clear();
  }

  /**
   * Informações sobre cache para debug
   */
  getCacheInfo() {
    return {
      size: this.signalCache.size,
      keys: Array.from(this.signalCache.keys()),
      language: this.i18nService.idioma().code
    };
  }

  /**
   * Pre-carrega traduções para melhor performance
   * @param keys Array de chaves para pré-carregar
   */
  preload(keys: string[]) {
    keys.forEach(key => this.t(key));
  }
}
